import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Member, MemberStatus } from '@/types/members';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreVertical, Edit, Mail, Trash2 } from 'lucide-react';

interface MemberCardMobileProps {
  member: Member;
  onEdit: (member: Member) => void;
  onResend: (member: Member) => void;
  onRemove: (member: Member) => void;
}

export function MemberCardMobile({ member, onEdit, onResend, onRemove }: MemberCardMobileProps) {
  useLanguage();
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      'administrador': 'Administrador',
      'editor': 'Editor',
      'visualizador': 'Visualizador',
    };
    return labels[role] || role;
  };

  const getStatusLabel = (status: MemberStatus) => {
    const labels: Record<MemberStatus, string> = {
      'ativo': 'Ativo',
      'pendente': 'Pendente',
      'expirado': 'Expirado',
    };
    return labels[status] || status;
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'administrador':
        return 'bg-[#dbe8ff] text-[#2a59d1] hover:bg-[#dbe8ff]';
      case 'editor':
        return 'bg-[#f1e9ff] text-[#7d46ff] hover:bg-[#f1e9ff]';
      case 'visualizador':
        return 'bg-[#eaeaea] text-[#666] hover:bg-[#eaeaea]';
      default:
        return '';
    }
  };

  const getStatusBadgeClass = (status: MemberStatus) => {
    switch (status) {
      case 'ativo':
        return 'bg-[#dbe8ff] text-[#2a59d1] hover:bg-[#dbe8ff]';
      case 'pendente':
        return 'bg-[#ffe9d6] text-[#d88818] hover:bg-[#ffe9d6]';
      case 'expirado':
        return 'bg-[#ffe1e1] text-[#d34646] hover:bg-[#ffe1e1]';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-[14px] border border-[#E6EAF0] p-[14px] hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-[#E8F0FF] flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-semibold text-[#556ff8]">
            {getInitials(member.name)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold text-[#1E293B]">{member.name}</h3>
            
            {/* Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 -mt-1 -mr-1">
                  <MoreVertical className="h-4 w-4 text-[#64748B]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onEdit(member)} className="cursor-pointer">
                  <Edit className="mr-2 h-4 w-4" />
                  {translateInterfaceText("Editar permissões")}</DropdownMenuItem>
                {(member.status === 'pendente' || member.status === 'expirado') && (
                  <DropdownMenuItem onClick={() => onResend(member)} className="cursor-pointer">
                    <Mail className="mr-2 h-4 w-4" />
                    {translateInterfaceText("Reenviar convite")}</DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => onRemove(member)}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {translateInterfaceText("Remover membro")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge className={`${getRoleBadgeClass(member.role)} text-xs font-medium`}>
              {getRoleLabel(member.role)}
            </Badge>
            <span className="text-[#94A3B8]">•</span>
            <Badge className={`${getStatusBadgeClass(member.status)} text-xs font-medium`}>
              {getStatusLabel(member.status)}
            </Badge>
          </div>

          {/* Details */}
          <div className="space-y-1">
            <p className="text-xs text-[#64748B]">
              {translateInterfaceText("Email:")}{" "}<span className="text-[#475569]">{member.email}</span>
            </p>
            <p className="text-xs text-[#64748B]">
              {translateInterfaceText("Convidado em:")}{" "}<span className="text-[#475569]">{member.invited_at}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
